// =============================================
// ContentPreviewDialog — podgląd LIVE artykułu z WordPress.
// Pobiera post.content.rendered + post.title.rendered przez REST proxy, renderuje
// w sandboxed iframe (zero XSS risk). Bez edycji — tylko view + przycisk "Otwórz w WP".
// =============================================

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { wordpressService } from "@/services/wordpressService";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string | null;
  title: string;
  wpPostUrl: string | null;
}

export function ContentPreviewDialog({ open, onOpenChange, nodeId, title, wpPostUrl }: Props) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !nodeId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      setHtml("");

      try {
        // Pobierz wp_post_id + WP credentials
        const { data: content } = await (supabase as any)
          .from("thematic_node_content")
          .select("wp_post_id, final_html")
          .eq("node_id", nodeId)
          .single();

        const { data: node } = await (supabase as any)
          .from("thematic_nodes")
          .select("map_id")
          .eq("id", nodeId)
          .single();
        if (!node) throw new Error("Node nie znaleziony");

        const { data: map } = await (supabase as any)
          .from("thematic_maps")
          .select("website_id")
          .eq("id", node.map_id)
          .single();
        if (!map?.website_id) throw new Error("Brak website_id");

        const { data: website } = await (supabase as any)
          .from("websites")
          .select("url, login, password, api_key")
          .eq("id", map.website_id)
          .single();

        if (cancelled) return;

        // Najpierw spróbuj LIVE WP (z schema, Yoast, theme HTML)
        if (content?.wp_post_id && website?.url && website?.login && (website?.api_key || website?.password)) {
          const prevCreds = wordpressService.getCredentials();
          wordpressService.setCredentials({
            url: website.url.replace(/\/$/, ""),
            username: website.login,
            password: website.api_key || website.password,
          });
          try {
            const res = await wordpressService.fetchPostForAudit(content.wp_post_id);
            if (!cancelled && res.success && res.data) {
              const post = res.data as Record<string, unknown>;
              const contentObj = post.content as { rendered?: string } | undefined;
              const titleObj = post.title as { rendered?: string } | undefined;
              if (contentObj?.rendered) {
                // Wrap z H1 + bazowe styling iframe (sandbox bez external stylesheet)
                const liveHeader = titleObj?.rendered || title;
                setHtml(buildIframeDoc(liveHeader, contentObj.rendered));
                return;
              }
            }
          } finally {
            if (prevCreds) wordpressService.setCredentials(prevCreds);
            else wordpressService.setCredentials(null as never);
          }
        }

        // Fallback do DB final_html
        if (content?.final_html) {
          setHtml(buildIframeDoc(title, content.final_html));
          return;
        }

        throw new Error("Brak final_html w DB i fetch z WP nieudany");
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Nieznany błąd");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, nodeId, title]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Podgląd artykułu
          </DialogTitle>
          <DialogDescription className="line-clamp-1">{title}</DialogDescription>
          <div className="flex items-center gap-2 pt-2">
            {wpPostUrl && (
              <Button asChild variant="outline" size="sm">
                <a href={wpPostUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Otwórz live na blogu
                </a>
              </Button>
            )}
            {wpPostUrl && (
              <Button asChild variant="outline" size="sm">
                <a
                  href={buildWpEditUrl(wpPostUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Edytuj w panelu WP admin"
                >
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Edytuj w WP
                </a>
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden border rounded-md mt-2 bg-white dark:bg-zinc-900">
          {loading && (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Pobieram artykuł z WordPress...
            </div>
          )}
          {error && !loading && (
            <div className="p-4 text-sm text-red-600 dark:text-red-400">
              Błąd podglądu: {error}
            </div>
          )}
          {html && !loading && (
            <iframe
              title="Podgląd treści"
              sandbox="allow-same-origin"
              srcDoc={html}
              className="w-full h-[70vh] border-0"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function buildIframeDoc(title: string, contentHtml: string): string {
  // Sandbox iframe z minimal styling. NIE wykonujemy JS z content (sandbox bez allow-scripts).
  return `<!DOCTYPE html>
<html lang="pl">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  body { font-family: system-ui, -apple-system, sans-serif; max-width: 800px; margin: 1rem auto; padding: 1rem; line-height: 1.6; color: #1a1a1a; background: #fff; }
  h1 { font-size: 1.875rem; margin-top: 0; line-height: 1.3; }
  h2 { font-size: 1.5rem; margin-top: 2rem; }
  h3 { font-size: 1.25rem; margin-top: 1.5rem; }
  p { margin: 1em 0; }
  a { color: #2563eb; text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  blockquote { border-left: 4px solid #e5e7eb; padding-left: 1rem; margin-left: 0; color: #4b5563; }
  pre, code { background: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-size: 0.875em; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #e5e7eb; padding: 0.5rem; text-align: left; }
  ul, ol { padding-left: 1.5rem; }
  hr { border: 0; border-top: 1px solid #e5e7eb; margin: 2rem 0; }
  sup { font-size: 0.75em; }
</style>
</head>
<body>
<h1>${escapeHtml(title)}</h1>
${contentHtml}
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[ch] || ch);
}

function buildWpEditUrl(wpPostUrl: string): string {
  // https://growthhacker.pl/slug/ → https://growthhacker.pl/wp-admin/edit.php
  try {
    const u = new URL(wpPostUrl);
    return `${u.origin}/wp-admin/edit.php`;
  } catch {
    return wpPostUrl;
  }
}
